import * as React from 'react';

import styled from '@emotion/styled';
import { isEmpty } from 'lodash';
import { transparentize } from 'polished';
import { ScheduleIcon } from 'react-magma-icons';

import { ThemeContext } from '../../theme/ThemeContext';
import { Announce } from '../Announce';
import { AmPmToggle } from './AmPmToggle';
import { useTimePicker, UseTimePickerProps } from './useTimePicker';
import { I18nContext } from '../../i18n';
import { useIsInverse } from '../../inverse';
import { ThemeInterface } from '../../theme/magma';
import { handleNumericBeforeInput, labelSuffix } from '../../utils';
import {
  FormFieldContainer,
  getInputFormFieldColors,
} from '../FormFieldContainer';
import { inputWrapperStyles } from '../InputBase';
import { VisuallyHidden } from '../VisuallyHidden';

export interface TimePickerProps extends UseTimePickerProps {
  /**
   * Style properties for the outer input
   */
  inputStyle?: React.CSSProperties;
}

interface StyledNumInputProps {
  disabled?: boolean;
  isFocused?: boolean;
  isInverse?: boolean;
  size?: number;
}

const InputsContainer = styled.div<{
  disabled?: boolean;
  hasError?: boolean;
  isInverse?: boolean;
}>`
  ${inputWrapperStyles};
  height: ${props => props.theme.spaceScale.spacing09};
  padding: ${props => props.theme.spaceScale.spacing03};
  min-width: 144px;
  width: fit-content;
  font-family: ${props => props.theme.bodyFont};
`;

const getDividerColor = (
  isInverse: boolean,
  isFocused: boolean,
  theme: ThemeInterface
): string => {
  if (isInverse) {
    return isFocused ? theme.colors.neutral0 : theme.colors.neutral500;
  }

  return isFocused ? theme.colors.brand.navy : theme.colors.neutral700;
};

export const getInputColor = (
  isInverse: boolean,
  isFocused: boolean,
  theme: ThemeInterface
): string => {
  if (isInverse) {
    return isFocused ? theme.colors.neutral0 : theme.colors.neutral500;
  }

  return isFocused ? theme.colors.brand.navy : theme.colors.neutral700;
};

export const Divider = styled.span<{
  disabled?: boolean;
  isInverse?: boolean;
  isFocused?: boolean;
}>`
  display: inline-block;
  position: relative;
  top: ${props => `-${props.theme.spaceScale.spacing01}`};
  color: ${props =>
    props.disabled
      ? props.isInverse
        ? props.theme.colors.neutral700
        : props.theme.colors.neutral500
      : getDividerColor(props.isInverse, props.isFocused, props.theme)};
`;

export const StyledNumInput = styled.input<StyledNumInputProps>`
  padding: 0;
  border: 0;
  text-align: center;
  text-align-last: center;
  min-width: ${props => (props.size ? `${props.size}ch` : 'auto')};
  max-width: ${props => (props.size ? `${props.size}ch` : 'auto')};
  width: ${props => props.theme.spaceScale.spacing06};
  color: ${props =>
    props.disabled
      ? props.isInverse
        ? props.theme.colors.neutral700
        : props.theme.colors.neutral500
      : props.isInverse
        ? props.theme.colors.neutral0
        : props.theme.colors.brand.navy};
  background: transparent;
  border-bottom: 2px solid transparent; // Reserve space for border when focused
  caret-color: transparent;

  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  -moz-appearance: textfield;

  &::placeholder {
    color: ${props =>
      props.disabled
        ? props.isInverse
          ? props.theme.colors.neutral700
          : props.theme.colors.neutral500
        : getInputColor(props.isInverse, props.isFocused, props.theme)};
  }

  &:focus {
    outline: 0;
    border-bottom: 2px solid
      ${props =>
        props.isInverse
          ? props.theme.colors.blue200
          : props.theme.colors.blue500};
    background: ${props =>
      props.isInverse
        ? props.theme.colors.blue700
        : transparentize(0.2, props.theme.colors.blue200)};
    color: ${props =>
      props.isInverse
        ? props.theme.colors.neutral0
        : props.theme.colors.brand.navy};

    &::selection {
      background: ${props =>
        props.isInverse
          ? props.theme.colors.blue700
          : transparentize(1, props.theme.colors.blue200)};
    }
  }
`;

const InputsWithTimezone = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spaceScale.spacing03};
`;

export const TimePicker = React.forwardRef<HTMLInputElement, TimePickerProps>(
  (props, ref) => {
    const theme = React.useContext(ThemeContext);
    const i18n = React.useContext(I18nContext);
    const [isFocused, setIsFocused] = React.useState(false);

    const {
      containerStyle,
      disabled,
      errorMessage,
      helperMessage,
      inputStyle,
      labelPosition,
      labelText,
      labelWidth,
      minutesStep,
      timezone,
      onChange,
      ...other
    } = props;

    const isInverse = useIsInverse(props.isInverse);

    const {
      am,
      amPm,
      amPmRef,
      descriptionId,
      hour,
      hourId,
      hourRef,
      id,
      time,
      minute,
      minuteId,
      minuteRef,
      toggleAmPm,
      handleHourChange,
      handleMinuteChange,
      handleHourKeyDown,
      handleMinuteKeyDown,
      handleAmPmKeyDown,
    } = useTimePicker(props);

    const hoursLabel = i18n.timePicker.hoursAriaLabel;
    const minutesLabel = i18n.timePicker.minutesAriaLabel;
    const amPmLabel =
      amPm === am
        ? i18n.timePicker.amButtonAriaLabel
        : i18n.timePicker.pmButtonAriaLabel;
    const hasTime = !isEmpty(hour) || !isEmpty(minute);

    return (
      <FormFieldContainer
        {...getInputFormFieldColors(theme, isInverse)}
        {...other}
        containerStyle={containerStyle}
        errorMessage={errorMessage}
        fieldId={id}
        helperMessage={helperMessage}
        isInverse={isInverse}
        labelText={labelText}
        labelPosition={labelPosition}
        labelWidth={labelWidth}
      >
        <InputsWithTimezone theme={theme}>
          <InputsContainer
            aria-disabled={disabled}
            data-testid="time-picker-input"
            disabled={disabled}
            isInverse={isInverse}
            hasError={!!errorMessage}
            theme={theme}
            style={inputStyle}
            role="group"
            aria-labelledby={`${id}${labelSuffix}`}
          >
            <ScheduleIcon
              color={
                disabled
                  ? isInverse
                    ? theme.colors.neutral700
                    : theme.colors.neutral500
                  : theme.colors.neutral600
              }
              style={{ marginRight: theme.spaceScale.spacing02 }}
            />
            <StyledNumInput
              aria-label={hoursLabel}
              aria-describedby={descriptionId}
              aria-valuemax={12}
              aria-valuemin={1}
              aria-valuenow={hour ? Number(hour) : undefined}
              data-testid="hoursTimeInput"
              disabled={disabled}
              id={hourId}
              isInverse={isInverse}
              isFocused={hasTime || isFocused}
              onChange={handleHourChange}
              onBeforeInput={handleNumericBeforeInput}
              onKeyDown={e => handleHourKeyDown(e, handleHourChange)}
              placeholder="--"
              ref={hourRef}
              theme={theme}
              type="text"
              value={hour}
              inputMode="numeric"
              pattern="[0-9]*"
              role="spinbutton"
              onFocus={e => {
                e.target.select();
                setIsFocused(true);
              }}
              onBlur={() => setIsFocused(false)}
            />
            <Divider
              disabled={disabled}
              isInverse={isInverse}
              isFocused={hasTime || isFocused}
              theme={theme}
            >
              :
            </Divider>
            <StyledNumInput
              aria-label={minutesLabel}
              aria-valuemax={59}
              aria-valuemin={0}
              aria-valuenow={minute ? Number(minute) : undefined}
              data-testid="minutesTimeInput"
              disabled={disabled}
              id={minuteId}
              isInverse={isInverse}
              isFocused={hasTime || isFocused}
              onChange={handleMinuteChange}
              onBeforeInput={handleNumericBeforeInput}
              onKeyDown={e => handleMinuteKeyDown(e, handleMinuteChange)}
              placeholder="--"
              ref={minuteRef}
              step={minutesStep || 1}
              theme={theme}
              type="text"
              value={minute}
              inputMode="numeric"
              pattern="[0-9]*"
              role="spinbutton"
              onFocus={e => {
                e.target.select();
                setIsFocused(true);
              }}
              onBlur={() => setIsFocused(false)}
            />
            <AmPmToggle
              aria-label={amPmLabel}
              disabled={disabled}
              isInverse={isInverse}
              ref={amPmRef}
              onClick={toggleAmPm}
              onKeyDown={handleAmPmKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              isFocused={hasTime || isFocused}
            >
              {amPm}
            </AmPmToggle>
            <VisuallyHidden>
              <Announce>
                {amPm === am
                  ? i18n.timePicker.amSelectedAnnounce
                  : i18n.timePicker.pmSelectedAnnounce}
              </Announce>
            </VisuallyHidden>
          </InputsContainer>
          {timezone}
        </InputsWithTimezone>
        <input
          disabled={disabled}
          id={id}
          ref={ref}
          type="hidden"
          value={time}
        />
      </FormFieldContainer>
    );
  }
);
